import 'react-toastify/dist/ReactToastify.css';

import { errorToast } from '../../../components/common/Toast';
import { authNewStudentService } from '../../auth';
import { apiUrl } from '../../../variables';
import chatService from '../../chat/chat.service';
import { disconnectWebSocket } from '../../../lib/websocket';
import {
  clearRefreshPromise,
  getRefreshPromise,
  setRefreshPromise,
} from './newStudentRequestSession';

export const authenticatedRequest = async (
  endpoint: string,
  method?: string,
  body?: object | FormData | null,
  type?: 'file' | 'object',
) => {
  const bearer = await authNewStudentService.getLoggedUser();
  if (!bearer) {
    return {
      result: false,
      message: ['Sessão inválida. Faça login novamente.'],
      data: [],
    };
  }

  const headers = new Headers({
    Authorization: `Bearer ${bearer}`,
  });

  let bodyRequest;

  if (type === 'file') {
    bodyRequest = body;
  } else {
    bodyRequest = body ? JSON.stringify(body) : null;
    headers.set('Content-Type', 'application/json');
  }

  const requestOptions: RequestInit = {
    method: method,
    mode: 'cors',
    headers: headers,
    body: bodyRequest,
  };

  const defaultResponse = { data: [], result: false, message: [''] };

  const lastRefresh = localStorage.getItem('lastRefreshToken');

  /** > 1 ¹/² Hora  desloga */
  if (lastRefresh && +lastRefresh + 1000 * 5400 < new Date().getTime())
    return authNewStudentService.cleanLoggerUser(true);

  async function resolveRefreshToken() {
    if (!getRefreshPromise()) {
      setRefreshPromise(authNewStudentService.refreshToken());
    }
    const newToken = await getRefreshPromise();
    clearRefreshPromise();
    return newToken;
  }

  async function endSessionAfterRefreshFailure() {
    await chatService.updateOnlineStatus(false);
    await disconnectWebSocket();
    window.dispatchEvent(new Event('token-expired'));
    authNewStudentService.cleanLoggerUser(true);
    return defaultResponse;
  }

  /** Função para repetir a request se tiver com o token expirado */
  async function repeatedRequest(
    newToken: {
      result?: boolean;
      data?: { access_token?: string };
      message?: string[];
    } | null | undefined,
  ) {
    if (!newToken?.result || !newToken?.data?.access_token) {
      return endSessionAfterRefreshFailure();
    }

    const refreshedHeaders = new Headers({
      Authorization: `Bearer ${newToken.data.access_token}`,
    });

    if (type !== 'file') {
      refreshedHeaders.set('Content-Type', 'application/json');
    }

    const refreshedRequestOptions: RequestInit = {
      method: method,
      mode: 'cors',
      headers: refreshedHeaders,
      body: bodyRequest,
    };

    const refreshedResponse = await fetch(
      apiUrl + endpoint,
      refreshedRequestOptions,
    );

    if (refreshedResponse.status === 401 || refreshedResponse.status === 441) {
      return endSessionAfterRefreshFailure();
    }

    if (refreshedResponse.status === 442) {
      return endSessionAfterRefreshFailure();
    }

    return refreshedResponse.json();
  }

  // > 45 min && < 1 ¹/² hora refreshToken
  if (
    lastRefresh &&
    +lastRefresh + 1000 * 2700 < new Date().getTime() &&
    +lastRefresh + 1000 * 5400 > new Date().getTime()
  ) {
    const newToken = await resolveRefreshToken();
    return await repeatedRequest(newToken);
  }

  const response = await fetch(apiUrl + endpoint, requestOptions);

  if (response.status === 401 || response.status === 441) {
    const errorBody = await response.json().catch(() => ({}));
    const errorMessage =
      typeof errorBody?.message === 'string'
        ? errorBody.message
        : Array.isArray(errorBody?.message)
          ? errorBody.message[0]
          : 'Token inválido';

    await endSessionAfterRefreshFailure();

    return {
      result: false,
      message: [errorMessage],
      data: [],
    };
  }

  if (response.status === 442) {
    const newToken = await resolveRefreshToken();
    return await repeatedRequest(newToken);
  }

  if (response.status === 404 || response.status === 500) {
    return defaultResponse;
  }

  return response.json();
};

export const unauthenticatedRequest = async (
  endpoint: string,
  method?: string,
  body?: string | object,
) => {
  const emptyFail = {
    data: null,
    result: false,
    message: ['Não foi possível conectar ao servidor. Tente novamente.'],
  };

  try {
    const res = await fetch(apiUrl + endpoint, {
      method: method,
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : null,
    });

    if (res.status === 404 || res.status === 500) {
      return {
        data: null,
        result: false,
        message: ['Serviço indisponível. Tente novamente mais tarde.'],
      };
    }

    try {
      return await res.json();
    } catch {
      return emptyFail;
    }
  } catch (error) {
    errorToast(error, 1);
    return emptyFail;
  }
};

export const changeObjectToFormData = (body) => {
  const formDataFormat = new FormData();
  for (const key in body) {
    if (body[key] instanceof Blob) {
      const fileBlob = new Blob([body[key]], { type: body[key].type });
      formDataFormat.append(key, fileBlob);
    } else if (body[key]) {
      formDataFormat.append(key, String(body[key]));
    }
  }
  return formDataFormat;
};
